import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import AppContainer from "../../common/app_container";
import Alert, {AlertProps} from "../../common/alert";
import {CreatorApplication} from "@src/model/creator_application";
import {useCreatorApplicationAPI} from "@src/api/creator_application_api";
import BackButton from "@src/common/components/back-button";

const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "N/A";
    try {
        return new Date(dateString).toLocaleDateString("en-US", {month: "numeric", day: "numeric", year: "numeric"});
    } catch (e) {
        return "Invalid Date";
    }
};

// Normalize status to handle bad data (e.g., REAL_ESTATE -> real_estate)
const normalizeStatus = (status: string): string => {
    if (!status) return status;
    // Convert to lowercase, but preserve common status formats
    const upperStatus = status.toUpperCase();
    if (upperStatus === "PENDING" || upperStatus === "PAYMENT_INITIATED" ||
        upperStatus === "APPROVED" || upperStatus === "REJECTED" ||
        upperStatus === "PAYMENT_COMPLETE") {
        return upperStatus;
    }
    // For other statuses (like REAL_ESTATE), convert to lowercase
    return status.toLowerCase();
};

// Check if status changes are allowed (not APPROVED and not PAYMENT_COMPLETE)
const canChangeStatus = (status: string): boolean => {
    const normalizedStatus = normalizeStatus(status);
    const upperStatus = normalizedStatus.toUpperCase();
    return upperStatus !== "APPROVED" && upperStatus !== "PAYMENT_COMPLETE";
};

const CreatorApplicationReview: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const {
        getApplication,
        updateApplicationStatus,
        deleteApplication,
    } = useCreatorApplicationAPI();
    const navigate = useNavigate();
    const [application, setApplication] = useState<CreatorApplication | null>(null);
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState<string | boolean>(false);
    const [onboardingData, setOnboardingData] = useState<any>(null);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            setStatus(null);
            getApplication(id)
                .then((app) => {
                    setApplication(app);
                    // Parse onboarding_data if it exists
                    if (app.onboarding_data) {
                        try {
                            setOnboardingData(JSON.parse(app.onboarding_data));
                        } catch (e) {
                            console.error("Failed to parse onboarding_data:", e);
                        }
                    }
                })
                .catch((err: any) => setStatus({type: "error", message: `Error loading application: ${err.message}`}))
                .finally(() => setIsLoading(false));
        } else {
            setStatus({type: "error", message: "Application ID is missing."});
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleStatusUpdate = async (newStatus: string) => {
        if (!application) return;
        setIsActionLoading(newStatus);
        setStatus(null);
        try {
            const updatedApp = await updateApplicationStatus(application.id, newStatus);
            setApplication(updatedApp);
            setStatus({type: "success", message: `Application status updated to ${newStatus}.`});
        } catch (err: any) {
            setStatus({type: "error", message: `Error updating status: ${err.message}`});
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!application || !window.confirm("Are you sure you want to reset this application? This will erase existing data and require the user to fill out the application again.")) return;
        setIsActionLoading("delete");
        setStatus(null);
        try {
            await deleteApplication(application.id);
            setStatus({type: "success", message: "Application deleted successfully."});
            navigate("/creator-applications");
        } catch (err: any) {
            setStatus({type: "error", message: `Error deleting application: ${err.message}`});
            setIsActionLoading(false);
        }
    };

    if (isLoading) {
        return <AppContainer>
            <div className="p-6">Loading application details...</div>
        </AppContainer>;
    }

    if (!application) {
        return (
            <AppContainer>
                <div className="p-6">
                    {status && <Alert {...status} onClose={() => setStatus(null)}/>}
                    {!status && <p>Application not found or ID is invalid.</p>}
                    <BackButton variant="secondary" onClick={() => navigate("/creator-applications")}/>
                </div>
            </AppContainer>
        );
    }

    const applicantFullName = [application.first_name, application.last_name].filter(Boolean).join(" ") || "N/A";
    const statusChangeAllowed = canChangeStatus(application.status);

    return (
        <AppContainer>
            <BackButton variant="ghost" onClick={() => navigate("/creator-applications")}/>
            <div className="p-6">
                <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
                    <h1 className="text-2xl font-semibold">{applicantFullName}</h1>
                    {statusChangeAllowed && (
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => {
                                    if (window.confirm("Are you sure you want to reject this application?")) {
                                        handleStatusUpdate("REJECTED");
                                    }
                                }}
                                disabled={!!isActionLoading}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 text-sm flex gap-4 items-center"
                            >
                                Reject
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={!!isActionLoading}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm flex gap-4 items-center"
                            >
                                Reset
                            </button>
                        </div>
                    )}
                </div>

                {status && <div className="mb-4"><Alert {...status} onClose={() => setStatus(null)}/></div>}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-1 bg-white p-4 rounded shadow">
                        <h2 className="text-lg font-semibold mb-4 border-b pb-2">Personal Information</h2>
                        <div className="space-y-2 text-sm">
                            <p><strong>Full Name:</strong> {applicantFullName}</p>
                            <p><strong>Email Address:</strong> {application.email}</p>
                            <p>
                                <strong>Status:</strong>
                                <span
                                    className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${(() => {
                                        const normalizedStatus = normalizeStatus(application.status);
                                        const upperStatus = normalizedStatus.toUpperCase();
                                        return upperStatus === "APPROVED"
                                            ? "bg-green-100 text-green-700"
                                            : upperStatus === "PAYMENT_COMPLETE"
                                                ? "bg-green-100 text-green-700"
                                                : upperStatus === "PAYMENT_INITIATED"
                                                    ? "bg-yellow-100 text-yellow-700"
                                                    : upperStatus === "REJECTED"
                                                        ? "bg-red-100 text-red-700"
                                                        : "bg-blue-100 text-blue-700";
                                    })()}`}
                                >
                                    {normalizeStatus(application.status)}
                                </span>
                            </p>
                            {(() => {
                                const normalizedStatus = normalizeStatus(application.status);
                                const upperStatus = normalizedStatus.toUpperCase();
                                if (upperStatus === "APPROVED" && application.user_id) {
                                    return (
                                        <p>
                                            <strong>User ID:</strong>{" "}
                                            <a
                                                href={`/users/edit/${application.user_id}`}
                                                className="text-blue-600 hover:underline"
                                            >
                                                {application.user_id}
                                            </a>
                                        </p>
                                    );
                                }
                                return null;
                            })()}
                            <p><strong>Submitted:</strong> {formatDate(application.created_at)}</p>
                            <p><strong>Last Updated:</strong> {formatDate(application.updated_at)}</p>
                        </div>
                    </div>

                    <div className="md:col-span-1 bg-white p-4 rounded shadow">
                        <h2 className="text-lg font-semibold mb-4 border-b pb-2">Payment Information</h2>
                        <div className="space-y-2 text-sm">
                            <p><strong>Payment Session ID:</strong> {application.payment_session_id || "N/A"}</p>
                            <p><strong>Payment Intent ID:</strong> {application.payment_intent_id || "N/A"}</p>
                            <p><strong>Stripe Product ID:</strong> {application.stripe_product_id || "N/A"}</p>
                            <p><strong>Stripe Price ID:</strong> {application.stripe_price_id || "N/A"}</p>
                        </div>
                    </div>

                    <div className="md:col-span-1 bg-white p-4 rounded shadow">
                        <h2 className="text-lg font-semibold mb-4 border-b pb-2">Application Details</h2>
                        <div className="space-y-2 text-sm">
                            <p><strong>Application ID:</strong> {application.id}</p>
                            <p><strong>IP Address:</strong> {application.ip_address ? (
                                <span className="font-mono text-xs">{application.ip_address}</span>
                            ) : "N/A"}</p>
                            <p><strong>User Agent:</strong> {application.user_agent ? (
                                <span
                                    className="text-xs break-all block mt-1 font-mono bg-gray-50 p-2 rounded">{application.user_agent}</span>
                            ) : "N/A"}</p>
                            <p><strong>Invitation Code:</strong> {application.invitation_code || "N/A"}</p>
                            <p><strong>Invitation Code ID:</strong> {application.invitation_code_id ? (
                                <a
                                    href={`/invitation-codes/edit/${application.invitation_code_id}`}
                                    className="text-blue-600 hover:underline"
                                >
                                    {application.invitation_code_id}
                                </a>
                            ) : "N/A"}</p>
                        </div>
                    </div>

                    {onboardingData && (
                        <div className="md:col-span-3 bg-white p-4 rounded shadow">
                            <h2 className="text-lg font-semibold mb-4 border-b pb-2">Onboarding Data</h2>
                            <div className="space-y-2 text-sm">
                                {Object.keys(onboardingData).length === 0 ? (
                                    <p className="text-gray-500 italic">No onboarding data available</p>
                                ) : (
                                    Object.entries(onboardingData)
                                        .filter(([_, value]) => value !== null && value !== undefined && value !== '')
                                        .map(([key, value]) => {
                                            // Format key: convert snake_case to Title Case
                                            const formattedKey = key
                                                .split('_')
                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                .join(' ');

                                            return (
                                                <p key={key}>
                                                    <strong>{formattedKey}:</strong> {String(value)}
                                                </p>
                                            );
                                        })
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AppContainer>
    );
};

export default CreatorApplicationReview;

